import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { teamsCallUrl, telUrl } from '../../utils/deepLinks';

/**
 * Modal to select call method when multiple options available
 * Options: Mobile phone, Business phone, Teams video call
 * Uses Portal to render at document body level (above all other elements)
 */
export function CallMethodModal({ employee, onClose }) {
  if (!employee) return null;

  const mobilePhone = employee.mobilePhone;
  const businessPhone = employee.businessPhones?.[0];
  const email = employee.mail;

  // Build list of available call methods
  const callMethods = [];

  if (mobilePhone) {
    callMethods.push({
      id: 'mobile',
      label: 'Mobile',
      number: mobilePhone,
      href: telUrl(mobilePhone),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      accent: '#22c55e',
    });
  }

  if (businessPhone) {
    callMethods.push({
      id: 'business',
      label: 'Business',
      number: businessPhone,
      href: telUrl(businessPhone),
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
        </svg>
      ),
      accent: '#3b82f6',
    });
  }

  if (email) {
    callMethods.push({
      id: 'teams',
      label: 'Teams Video',
      number: 'Microsoft Teams',
      href: teamsCallUrl(email),
      isExternal: true,
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
      ),
      accent: '#8b5cf6',
    });
  }

  const handleMethodClick = (method) => {
    if (method.isExternal) {
      window.open(method.href, '_blank', 'noopener,noreferrer');
    } else {
      window.location.href = method.href;
    }
    onClose();
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/50"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="rounded-card shadow-e3 w-full max-w-md overflow-hidden bg-surface border border-line"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={`Call ${employee.displayName}`}
        >
          {/* Header */}
          <div className="px-6 py-5 border-b border-line bg-surface-2">
            <h3 className="text-xl font-semibold text-ink">Call {employee.displayName?.split(' ')[0]}</h3>
            <p className="text-sm mt-1 text-ink-3">Choose how you'd like to connect</p>
          </div>

          {/* Call Methods */}
          <div className="p-4 space-y-3">
            {callMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => handleMethodClick(method)}
                className="w-full flex items-center gap-4 p-4 rounded-xl transition-colors text-left border border-line bg-surface hover:bg-surface-2 hover:border-line-strong"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: `${method.accent}14`,
                    color: method.accent,
                  }}
                >
                  {method.icon}
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-ink">{method.label}</p>
                  <p className="text-sm text-ink-3">{method.number}</p>
                </div>
                <svg className="w-5 h-5 flex-shrink-0 text-ink-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            ))}

            {callMethods.length === 0 && (
              <p className="text-center py-4 text-ink-3">No contact methods available</p>
            )}
          </div>

          {/* Cancel Button */}
          <div className="px-4 pb-4">
            <button
              onClick={onClose}
              className="w-full py-3 rounded-xl transition-colors font-medium text-ink-2 hover:bg-surface-2"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
